import type { BackgroundPattern, GradientPreset, Header } from "@keepoinc/site-page-react-component";

import { Site } from "./site";

/**
 * What's behind the page beyond its solid color (`primary`): as the site page component draws it
 * (@keepoinc/site-page-react-component `Background`). An image or video with no `url` yet is one being chosen.
 */
export type Background =
  | {
      type: "gradient";
      /** Custom: between two colors, in a direction (an angle, or from the middle when `radial`). */
      from?: string;
      to?: string;
      angle?: number;
      radial?: boolean;
      /** Ready-made: a shape in the theme's colors, moving slowly if `animate`. */
      preset?: GradientPreset;
      animate?: boolean;
    }
  | { type: "image"; url: string; dim?: number; /** 10-100 (%); none: 100 */ opacity?: number }
  | {
      type: "blur";
      /** How much the photo is blurred, 10-100 (%); none: 50. */
      amount?: number;
    }
  | {
      type: "pattern";
      pattern: BackgroundPattern;
      color: string;
      /** 5-60 (%); none: 15. */
      opacity?: number;
      /** "fixed": stays still; none or "scroll": goes up with the content. */
      position?: "fixed" | "scroll";
      /** none: medium. */
      size?: "small" | "medium" | "large";
    }
  | { type: "video"; url: string; poster?: string; dim?: number; /** 10-100 (%); none: 100 */ opacity?: number };

/** How buttons look; link cards keep their own (@keepoinc/site-page-react-component `ButtonStyle`). */
export type ButtonStyle = {
  variant?: "solid" | "glass" | "outline";
  corners?: "square" | "rounded" | "pill";
  /** Under the buttons: soft (diffuse) or hard (solid, to the side); none: none. */
  shadow?: "soft" | "hard";
};

export type Theme = {
  id: string;
  /** None: just the solid color. */
  background?: Background;
  /** The top (the Welcome's name and photo): hero, banner or a framed photo; none: classic, as always. */
  header?: Header;
  /** None: solid pills, as always. */
  buttonStyle?: ButtonStyle;
  /** The titles' own color; none: the text color (`secondary`). */
  titleColor?: string;
  primary: string;
  secondary: string;
  buttons: string;
  textButtons: string;
  editorPrimary?: string;
  cardColor: string;
  cardText: string;
  iconsColor: string;
  iconsBackground: string;
  /** The icons' look, the Keepo's default (each Redes sociais section keeps it too, as the published page reads it) */
  iconShape?: "ROUND" | "SQUARE";
  iconFill?: "FILLED" | "OUTLINE";
};

export const extraPredefinedTheme: Theme = {
  id: "10",
  primary: "#3E2E2E",
  secondary: "#C18D5D",
  buttons: "#C18D5D",
  textButtons: "#3E2E2E",
  cardColor: "#C18D5D",
  cardText: "#3E2E2E",
  iconsColor: "#3E2E2E",
  iconsBackground: "#C18D5D",
};

export const preDefinedThemes: Theme[] = [
  {
    id: "1",
    primary: "#FFFFFF",
    secondary: "#181A20",
    buttons: "#181A20",
    textButtons: "#FFFFFF",
    cardColor: "#181A20",
    cardText: "#FFFFFF",
    iconsColor: "#FFFFFF",
    iconsBackground: "#181A20",
  },
  {
    id: "6",
    primary: "#181A20",
    secondary: "#FFFFFF",
    buttons: "#FFFFFF",
    textButtons: "#181A20",
    editorPrimary: "#262A34",
    cardColor: "#FFFFFF",
    cardText: "#181A20",
    iconsColor: "#181A20",
    iconsBackground: "#FFFFFF",
  },
  {
    id: "4",
    primary: "#FDFFE7",
    secondary: "#FF4040",
    buttons: "#FF4040",
    textButtons: "#FDFFE7",
    cardColor: "#FF4040",
    cardText: "#FDFFE7",
    iconsColor: "#FDFFE7",
    iconsBackground: "#FF4040",
  },
  {
    id: "7",
    primary: "#FF4040",
    secondary: "#FFFFFF",
    buttons: "#FFFFFF",
    textButtons: "#FF4040",
    cardColor: "#FFFFFF",
    cardText: "#FF4040",
    iconsColor: "#FF4040",
    iconsBackground: "#FFFFFF",
  },
  {
    id: "5",
    primary: "#61174C",
    secondary: "#FFFFFF",
    buttons: "#FFFFFF",
    textButtons: "#61174C",
    cardColor: "#FFFFFF",
    cardText: "#61174C",
    iconsColor: "#61174C",
    iconsBackground: "#FFFFFF",
  },
  {
    id: "2",
    primary: "#1DA6A8",
    secondary: "#FFFFFF",
    buttons: "#FFFFFF",
    textButtons: "#1DA6A8",
    cardColor: "#FFFFFF",
    cardText: "#1DA6A8",
    iconsColor: "#1DA6A8",
    iconsBackground: "#FFFFFF",
  },
  {
    id: "8",
    primary: "#1B723D",
    secondary: "#FDD6D6",
    buttons: "#FDD6D6",
    textButtons: "#1B723D",
    cardColor: "#FDD6D6",
    cardText: "#1B723D",
    iconsColor: "#1B723D",
    iconsBackground: "#FDD6D6",
  },
  {
    id: "9",
    primary: "#FFEE51",
    secondary: "#030303",
    buttons: "#030303",
    textButtons: "#FFEE51",
    cardColor: "#030303",
    cardText: "#FFEE51",
    iconsColor: "#FFEE51",
    iconsBackground: "#030303",
  },
  {
    id: "3",
    primary: "#0F1D41",
    secondary: "#DCFFE6",
    buttons: "#DCFFE6",
    textButtons: "#0F1D41",
    cardColor: "#DCFFE6",
    cardText: "#0F1D41",
    iconsColor: "#0F1D41",
    iconsBackground: "#DCFFE6",
  },
];

const sanitizeTheme = (theme: Theme): Theme => {
  return {
    ...theme,
    cardColor: theme.secondary,
    cardText: theme.primary,
    iconsColor: theme.primary,
    iconsBackground: theme.secondary,
  };
};

const sanitizeCustomTheme = (site: Site): Theme => {
  return {
    id: "custom",
    primary: site.theme.primary || preDefinedThemes[0].primary,
    secondary: site.theme.secondary || preDefinedThemes[0].secondary,
    textButtons: site.theme.textButtons || preDefinedThemes[0].textButtons,
    buttons: site.theme.buttons || preDefinedThemes[0].buttons,
    cardColor: site.theme.cardColor || site.theme.secondary || preDefinedThemes[0].cardColor,
    cardText: site.theme.cardText || site.theme.primary || preDefinedThemes[0].cardText,
    iconsColor: site.theme.iconsColor || site.theme.primary || preDefinedThemes[0].iconsColor,
    iconsBackground: site.theme.iconsBackground || site.theme.secondary || preDefinedThemes[0].iconsBackground,
  };
};

export const getThemeFromSite = (site: Site): Theme => {
  const theme = [...preDefinedThemes, extraPredefinedTheme].find(({ id }) => id === site.theme.id);
  const colors = theme ? sanitizeTheme(theme) : sanitizeCustomTheme(site);
  // The background, the buttons' style, the titles' color, the top and the icons' look go with the theme
  return {
    ...colors,
    ...(site.theme.background && { background: site.theme.background }),
    ...(site.theme.buttonStyle && { buttonStyle: site.theme.buttonStyle }),
    ...(site.theme.titleColor && { titleColor: site.theme.titleColor }),
    ...(site.theme.header && { header: site.theme.header }),
    ...(site.theme.iconShape && { iconShape: site.theme.iconShape }),
    ...(site.theme.iconFill && { iconFill: site.theme.iconFill }),
  };
};

export const getCustomThemeFromSite = (site: Site): Theme => {
  return site.theme.id === "custom" ? sanitizeCustomTheme(site) : { ...preDefinedThemes[0], id: "custom" };
};
